import React from 'react';
import { useSelector } from 'react-redux';
import { selectLocale } from '../../redux/language/selectors';
import './styles.scss';
import IntlCrawlableLink from 'app/components/IntlCrawlableLink';
import { List } from 'semantic-ui-react';

/**
 * French Terms Of Use
 * @returns {JSX.Element}
 */
function RenderFrenchText() {
  return (
    <>
      <section id="introduction">
        <p>
          Les présentes conditions générales d'utilisation (dites «&nbsp;CGU&nbsp;») ont pour objet
          l'encadrement juridique des modalités de mise à disposition du site et des services
          par&nbsp;
          <a href="https://picoxia.com/fr/">picoxia.com</a> et de définir les conditions d'accès et
          d'utilisation des services par «&nbsp;l'Utilisateur&nbsp;».
        </p>
        <p>
          Les présentes CGU sont accessibles sur le site à la rubrique «&nbsp;
          <IntlCrawlableLink url="/terms-of-use">CGU</IntlCrawlableLink>&nbsp;».
        </p>
      </section>

      <section id="1-legal-notices">
        <h2>1.&nbsp;Mentions légales</h2>
        <section id="publisher" className="sub-section">
          <h3>Éditeur</h3>
          <List>
            <List.Item>Nom&nbsp;: Robin IMBACH</List.Item>
            <List.Item>
              Email&nbsp;:&nbsp;
              <a href="mailto:contact@picoxia.com" target="_blank">
                contact@picoxia.com
              </a>
            </List.Item>
          </List>
        </section>

        <section id="hosting" className="sub-section">
          <h3>Hébergement</h3>
          <List>
            <List.Item>Google Ireland Limited</List.Item>
            <List.Item>Gordon House, Barrow Street, Dublin 4</List.Item>
            <List.Item>Irlande</List.Item>
            <List.Item>
              N° de téléphone&nbsp;:&nbsp;
              <a href="tel:0035314361000" target="_blank">
                +353 1 436 1000
              </a>
            </List.Item>
          </List>
        </section>
      </section>

      <section id="2-access-to-the-site">
        <h2>2.&nbsp;Accés au site</h2>
        <p>
          Le site <a href="https://picoxia.com/fr/">picoxia.com</a> permet à l'Utilisateur un accès
          aux services suivants&nbsp;:
        </p>
        <ul>
          <li>
            <b>Aide à l'analyse des radiographies&nbsp;:</b> Le site offre une assistance à
            l'analyse des radiographies pour les carnivores domestiques, notamment les chiens et les
            chats.
            <br />
            Les analyses couvrent les zones suivantes&nbsp;:
            <ul>
              <li>Thorax (vue de face et de profil)</li>
              <li>Abdomen (vue de face et de profil)</li>
              <li>Pelvis (vue de face)</li>
            </ul>
            Notre service repose sur un algorithme de détection capable d'identifier 34 catégories
            distinctes de lésions, facilitant ainsi le diagnostic pour les professionnels
            vétérinaires.
          </li>
        </ul>
        <p>
          Le site est accessible gratuitement en tout lieu à tout Utilisateur ayant un accès à
          Internet. Tous les frais supportés par l'Utilisateur pour accéder au service (matériel
          informatique, logiciels, connexion Internet, etc.) sont à sa charge.
        </p>
      </section>

      <section id="3-data-collection">
        <h2>3.&nbsp;Collecte des données</h2>
        <p>
          Les images envoyées par l'Utilisateur sont sauvegardées pour la fonctionnalité
          «&nbsp;Historique&nbsp;» et pour l'amélioration du service d'interprétation. La
          fonctionnalité «&nbsp;Historique&nbsp;» permet à l'Utilisateur de consulter ses analyses
          passées, facilitant ainsi le suivi des diagnostics au fil du temps.
        </p>
        <p>
          Le site assure à l'Utilisateur une collecte et un traitement d'informations personnelles
          dans le respect de la vie privée conformément à la loi n°&nbsp;78-17 du 6 janvier 1978
          relative à l'informatique, aux fichiers et aux libertés. Elles ne font l'objet d'aucune
          cession à des tiers.
        </p>
        <p>
          Conformément à la loi «&nbsp;Informatique et Libertés&nbsp;» n°&nbsp;78-17 du 6 janvier
          1978, vous disposez d'un droit d'accès, de rectification et d'opposition aux données
          personnelles vous concernant. Pour l'exercer, il suffit de nous en faire la demande par
          courriel à l'adresse suivante&nbsp;:&nbsp;
          <a href="mailto:contact@picoxia.com" target="_blank">
            contact@picoxia.com
          </a>
        </p>
      </section>

      <section id="4-restrictions-on-use-specific-to-picoxia-ai">
        <h2>4.&nbsp;Restrictions d'Utilisation Spécifiques à l'IA Picoxia</h2>
        <p>
          En utilisant les services fournis par Picoxia, vous acceptez de respecter les restrictions
          suivantes&nbsp;:
        </p>
        <ul>
          <li>
            <b>Interdiction de Concurrence&nbsp;:</b> Vous vous engagez à ne pas utiliser les
            services pour développer, directement ou indirectement, des modèles, produits ou
            services qui entreraient en concurrence avec ceux offerts par Picoxia. Toute utilisation
            des services à des fins concurrentielles est strictement interdite.
            <br />
            <br />
          </li>
          <li>
            <b>Interdiction d'Ingénierie Inverse&nbsp;:</b> Il est strictement interdit de tenter de
            faire de l'ingénierie inverse, d'extraire, de copier ou de reproduire tout composant des
            services, y compris, mais sans s'y limiter, les données, algorithmes, logiciels ou
            modèles sous-jacents (par exemple, les poids des paramètres). Toute tentative de
            décompilation, désassemblage ou autre méthode visant à découvrir le code source ou les
            processus des services est interdite.
            <br />
            <br />
          </li>
          <li>
            <b>Respect des Droits de Propriété Intellectuelle&nbsp;:</b> Vous reconnaissez que les
            services et tous les contenus associés sont protégés par les droits de propriété
            intellectuelle de Picoxia ou de ses concédants de licence. Vous vous engagez à respecter
            ces droits et à ne pas les violer de quelque manière que ce soit.
          </li>
        </ul>
        <p>
          Tout manquement à ces restrictions pourra entraîner la résiliation immédiate de votre
          accès aux services et pourra faire l'objet de poursuites judiciaires conformément aux lois
          applicables.
        </p>
      </section>

      <section id="5-intellectual-property-rights">
        <h2>5.&nbsp;Propriété intellectuelle</h2>
        <p>
          Les marques, logos, signes ainsi que tous les contenus du site (textes, images) font
          l'objet d'une protection par le Code de la propriété intellectuelle et plus
          particulièrement par le droit d'auteur.
        </p>
        <p>
          L'Utilisateur doit solliciter l'autorisation préalable du site pour toute reproduction,
          publication, copie des différents contenus. Il s'engage à une utilisation des contenus du
          site dans un cadre strictement privé, toute utilisation à des fins commerciales et
          publicitaires est strictement interdite. Toute représentation totale ou partielle de ce
          site par quelque procédé que ce soit, sans l'autorisation expresse de l'exploitant du site
          Internet constituerait une contrefaçon sanctionnée par l'article L. 335-2 et suivants du
          Code de la propriété intellectuelle.
        </p>
        <p>
          Il est rappelé conformément à l'article L. 122-5 du Code de propriété intellectuelle que
          l'Utilisateur qui reproduit, copie ou publie le contenu protégé doit citer l'auteur et sa
          source.
        </p>
      </section>

      <section id="6-responsibility">
        <h2>6.&nbsp;Responsabilité</h2>
        <p>
          Les résultats proposés par l'algorithme d'interprétation des radiographies ne sont pas
          exempts d'erreurs. L'Utilisateur prend entière responsabilité de son utilisation des
          résultats.
        </p>
        <p>
          L'algorithme de traitement d'image est destiné aux professionels de la profession
          vétérinaire uniquement. L'utilisation dudit algorithme par un Utilisateur non-vétérinaire
          est à son entière responsabilité.
        </p>
        <p>
          Les sources des informations diffusées sur le site&nbsp;
          <a href="https://picoxia.com/fr/">picoxia.com</a> sont réputées fiables mais le site ne
          garantit pas qu'il soit exempt de défauts, d'erreurs ou d'omissions. Les informations
          communiquées sont présentées à titre indicatif et général sans valeur contractuelle.
          Malgré des mises à jour régulières, le site&nbsp;
          <a href="https://picoxia.com/fr/">picoxia.com</a> ne peut être tenu responsable de la
          modification des dispositions administratives et juridiques survenant après la
          publication. De même, le site ne peut être tenu responsable de l'utilisation et de
          l'interprétation de l'information contenue dans ce site. Le site&nbsp;
          <a href="https://picoxia.com/fr/">picoxia.com</a> ne peut être tenu pour responsable
          d'éventuels virus qui pourraient infecter l'ordinateur ou tout matériel informatique de
          l'Internaute, suite à une utilisation, à l'accès, ou au téléchargement provenant de ce
          site. La responsabilité du site ne peut être engagée en cas de force majeure ou du fait
          imprévisible et insurmontable d'un tiers.
        </p>
      </section>

      <section id="7-cancel-subscription">
        <h2>7.&nbsp;Modalités de Résiliation de l'Abonnement PicoxIA</h2>
        <p>
          Chez Picoxia, nous nous engageons à offrir un service de qualité à nos clients. Nous
          comprenons que vos besoins peuvent évoluer, et vous pourriez souhaiter résilier votre
          abonnement. Voici les modalités de résiliation de votre abonnement PicoxIA conformément à
          nos conditions d'utilisation.
        </p>

        <ul>
          <li>
            <b>Procédure de Résiliation</b>
            <ul>
              <li>
                <b>Espace Client&nbsp;:</b> Résiliation Immédiate&nbsp;: Vous pouvez résilier votre
                abonnement directement depuis votre espace client en suivant les instructions dans
                la section «&nbsp;Abonnements et Crédits&nbsp;».
              </li>
              <li>
                <b>Demande Écrite&nbsp;:</b> Par Email&nbsp;: Envoyez une demande écrite de
                résiliation à l'adresse suivante&nbsp;:{' '}
                <a href="mailto:contact@picoxia.com" target="_blank">
                  contact@picoxia.com
                </a>
                .
                <ul>
                  <li>
                    <i>Informations Requises&nbsp;:</i> La demande doit être envoyée depuis l'email
                    associé à votre compte PicoxIA et inclure vos informations personnelles ainsi
                    que la date souhaitée pour la résiliation.
                  </li>
                </ul>
              </li>
            </ul>
          </li>
          <li>
            <b>Conséquences de la Résiliation</b>
            <ul>
              <li>
                <b>Accès au Service&nbsp;:</b> Votre accès au service PicoxIA sera désactivé à la
                date effective de résiliation. Vous ne pourrez plus accéder aux fonctionnalités et
                contenus réservés aux abonnés.
              </li>
              <li>
                <b>Remboursement&nbsp;:</b> Si vous avez payé à l'avance, vous pourriez être
                éligible à un remboursement partiel au prorata du temps utilisé.
              </li>
              <li>
                <b>Données Personnelles&nbsp;:</b> Conformément à notre politique de
                confidentialité, vos données personnelles seront traitées selon les réglementations
                en vigueur. Vous pouvez demander la suppression de vos données en contactant notre
                service client.
              </li>
            </ul>
          </li>
        </ul>
      </section>

      <section id="8-hypertext-links">
        <h2>8.&nbsp;Liens hypertextes</h2>
        <p>
          Des liens hypertextes peuvent être présents sur le site. L'Utilisateur est informé qu'en
          cliquant sur ces liens, il sortira du site&nbsp;
          <a href="https://picoxia.com/fr/">picoxia.com</a>. Ce dernier n'a pas de contrôle sur les
          pages web sur lesquelles aboutissent ces liens et ne saurait, en aucun cas, être
          responsable de leur contenu.
        </p>
      </section>

      <section id="9-cookies">
        <h2>9.&nbsp;Politique de Cookies</h2>
        <p>
          Le site <a href="https://picoxia.com/fr/">picoxia.com</a> utilise uniquement des cookies
          nécessaires à son fonctionnement. Ces cookies sont essentiels pour assurer la navigation
          et l'utilisation des fonctionnalités de base du site. Ils ne collectent pas d'informations
          personnelles et ne servent pas à des fins de suivi ou de marketing.
        </p>
        <p>
          En utilisant notre site, vous acceptez l'utilisation de ces cookies nécessaires. Pour
          toute question, contactez-nous à{' '}
          <a href="mailto:contact@picoxia.com" target="_blank">
            contact@picoxia.com
          </a>
          .
        </p>
      </section>

      <section id="10-terms-of-use-changes">
        <h2>10.&nbsp;Modifications des CGU</h2>
        <p>
          Picoxia se réserve le droit de modifier les présentes CGU à tout moment. Les modifications
          seront publiées sur le site avec une date d'entrée en vigueur. Il est de la responsabilité
          de l'Utilisateur de consulter régulièrement les CGU pour prendre connaissance des
          éventuelles modifications. L'utilisation continue du site après la publication des
          modifications vaudra acceptation des nouvelles CGU.
        </p>
      </section>

      <section id="11-security">
        <h2>11.&nbsp;Sécurité</h2>
        <p>
          Picoxia met en œuvre des mesures de sécurité techniques et organisationnelles pour
          protéger les données personnelles de l'Utilisateurs contre tout accès non autorisé,
          divulgation, altération ou destruction. Cependant, bien que nous nous efforcions de
          protéger vos informations, aucune méthode de transmission sur Internet ou de stockage
          électronique ne peut garantir une sécurité absolue.
        </p>
      </section>

      <section id="12-applicable-law-and-jurisdiction">
        <h2>12.&nbsp;Droit applicable et juridiction compétente</h2>
        <p>
          La législation française s'applique au présent contrat. En cas d'absence de résolution
          amiable d'un litige né entre les parties, les tribunaux français seront seuls compétents
          pour en connaître.
        </p>
        <p>
          Pour toute question relative à l'application des présentes CGU, contactez-nous à{' '}
          <a href="mailto:contact@picoxia.com" target="_blank">
            contact@picoxia.com
          </a>
          .
        </p>
      </section>
    </>
  );
}

/**
 * English Terms Of Use
 * @returns {JSX.Element}
 */
function RenderEnglishText() {
  return (
    <>
      <section id="introduction">
        <p>
          The purpose of these General Conditions of Use (hereinafter referred to as "GCU") is to
          provide a legal framework for the terms and conditions under which{' '}
          <a href="https://picoxia.com/en/">picoxia.com</a> makes the Site and its Services
          available and to define the conditions under which the "User" may access and use the
          Services.
        </p>
        <p>
          These Terms and Conditions are available on the Site under the heading "
          <IntlCrawlableLink url="/terms-of-use">GCU</IntlCrawlableLink>".
        </p>
      </section>

      <section id="1-legal-notices">
        <h2>1.&nbsp;Legal Notices</h2>
        <section id="publisher" className="sub-section">
          <h3>Publisher</h3>
          <List>
            <List.Item>Name: Robin IMBACH</List.Item>
            <List.Item>
              E-mail:&nbsp;
              <a href="mailto:contact@picoxia.com" target="_blank">
                contact@picoxia.com
              </a>
            </List.Item>
          </List>
        </section>

        <section id="hosting" className="sub-section">
          <h3>Hosting</h3>
          <List>
            <List.Item>Google Ireland Limited</List.Item>
            <List.Item>Gordon House, Barrow Street, Dublin 4</List.Item>
            <List.Item>Irelande</List.Item>
            <List.Item>
              Phone number:&nbsp;
              <a href="tel:0035314361000" target="_blank">
                +353 1 436 1000
              </a>
            </List.Item>
          </List>
        </section>
      </section>

      <section id="2-access-to-the-site">
        <h2>2.&nbsp;Access to the site</h2>
        <p>
          The <a href="https://picoxia.com/en/">picoxia.com</a> website gives the User access to the
          following services:
        </p>
        <ul>
          <li>
            <b>Assistance in the analysis of radiographs:</b> The site offers assistance in the
            analysis of radiographs of domestic carnivores, in particular dogs and cats.
            <br />
            The analyses cover the following areas:
            <ul>
              <li>Thorax (front and side view)</li>
              <li>Abdomen (front and side view)</li>
              <li>Pelvis (front view)</li>
            </ul>
            Our service is based on a detection algorithm capable of identifying 34 different
            categories of lesions, making diagnosis easier for veterinary professionals.
          </li>
        </ul>
        <p>
          The site is accessible free of charge from any location to any User with internet access.
          All costs incurred by the User in accessing the service (hardware, software, Internet
          connection, etc.) are at the User's expense.
        </p>
      </section>

      <section id="3-data-collection">
        <h2>3.&nbsp;Data collection</h2>
        <p>
          The images sent by the User are stored for the "History" function and to improve the
          interpretation service. The "History" function allows the User to view previous analyses,
          making it easier to monitor diagnoses over time.
        </p>
        <p>
          The Site guarantees that the User's personal data will be collected and processed in
          compliance with the French Data Protection Act no. 78-17 of 6 January 1978. This
          information will not be disclosed to third parties.
        </p>
        <p>
          In accordance with the French Data Protection Act no. 78-17 of 6 January 1978, you have
          the right to access, correct and oppose any personal data concerning you. To exercise this
          right, please send us a request by e-mail to the following address:&nbsp;
          <a href="mailto:contact@picoxia.com" target="_blank">
            contact@picoxia.com
          </a>
        </p>
      </section>

      <section id="4-restrictions-on-use-specific-to-picoxia-ai">
        <h2>4.&nbsp;Specific restrictions on use relating to AI Picoxia</h2>
        <p>By using the Picoxia services, you agree to comply with the following restrictions:</p>
        <ul>
          <li>
            <b>Prohibition of Competition:</b> You agree not to use the services to develop models,
            products or services that compete with those offered by Picoxia, either directly or
            indirectly. Any use of the services for competitive purposes is strictly prohibited.
            <br />
            <br />
          </li>
          <li>
            <b>Prohibition of reverse engineering:</b> Attempting to reverse engineer, extract, copy
            or reproduce any component of the services is strictly prohibited, including the
            underlying data, algorithms, software or models (e.g. parameter weights). Attempting to
            decompile, disassemble or otherwise discover the source code or processes of the
            services is also prohibited.
            <br />
            <br />
          </li>
          <li>
            <b>Respect for Intellectual Property Rights:</b> You acknowledge that the Services and
            all associated content are protected by the intellectual property rights of Picoxia or
            its licensors. You agree to respect these rights and not to infringe them in any way.
          </li>
        </ul>
        <p>
          Breaching these restrictions may result in immediate termination of your access to the
          Services and may result in legal action being taken against you in accordance with
          applicable laws.
        </p>
      </section>

      <section id="5-intellectual-property-rights">
        <h2>5.&nbsp;Intellectual property rights</h2>
        <p>
          The trademarks, logos, signs and all the contents of the Site (texts, images) are
          protected by the French Intellectual Property Code and, in particular, by copyright.
        </p>
        <p>
          The User must obtain prior authorisation from the Site for any reproduction, publication
          or copying of the various contents. The User undertakes to use the content of the Site for
          strictly private purposes only; any use for commercial or advertising purposes is strictly
          prohibited. Any total or partial representation of this site by any means whatsoever,
          without the express authorisation of the site operator, constitutes an infringement
          punishable under articles L. 335-2 et seq. of the French Intellectual Property Code.
        </p>
        <p>
          In accordance with article L. 122-5 of the French Intellectual Property Code, Users who
          reproduce, copy or publish protected content must indicate the author and source.
        </p>
      </section>

      <section id="6-responsibility">
        <h2>6.&nbsp;Responsibility</h2>
        <p>
          The results proposed by the X-ray interpretation algorithm are not error-free. The User
          assumes full responsibility for the use of the results.
        </p>
        <p>
          The image processing algorithm is intended for use by veterinary professionals only. The
          use of the said algorithm by a non-veterinary User is at his/her own risk.
        </p>
        <p>
          The sources of the information published on the{' '}
          <a href="https://picoxia.com/en/">picoxia.com</a> website are believed to be reliable, but
          the website does not guarantee that it is free from defects, errors or omissions. The
          information provided is for general guidance only and has no contractual value. Despite
          regular updates, the <a href="https://picoxia.com/en/">picoxia.com</a> website cannot be
          held responsible for any changes in administrative or legal provisions occurring after
          publication. Likewise, the site cannot be held responsible for the use and interpretation
          of the information contained in this site. The{' '}
          <a href="https://picoxia.com/en/">picoxia.com</a> site cannot be held liable for any
          viruses that may infect the computer or other equipment of the Internet User following use
          of, access to or downloading from this site. The site cannot be held liable in the event
          of force majeure or the unforeseeable and insurmountable act of a third party.
        </p>
      </section>

      <section id="7-cancel-subscription">
        <h2>7.&nbsp;How to cancel your PicoxIA subscription</h2>
        <p>
          At Picoxia, we are committed to providing a quality service to our customers. We
          understand that your needs may change and you may wish to cancel your subscription. Here
          are the procedures for cancelling your PicoxIA subscription in accordance with our terms
          and conditions.
        </p>

        <ul>
          <li>
            <b>Cancellation Procedure</b>
            <ul>
              <li>
                <b>Customer Area:</b> Immediate cancellation: You can cancel your subscription
                directly from your account by following the instructions in the "Subscriptions and
                Credits" section.
              </li>
              <li>
                <b>Written request:</b> By email: Send a written cancellation request to the
                following address:{' '}
                <a href="mailto:contact@picoxia.com" target="_blank">
                  contact@picoxia.com
                </a>
                .
                <ul>
                  <li>
                    <i>Information required:</i> The request must be sent from the email associated
                    with your PicoxIA account and must include your personal details and the desired
                    cancellation date.
                  </li>
                </ul>
              </li>
            </ul>
          </li>
          <li>
            <b>Consequences of cancelling</b>
            <ul>
              <li>
                <b>Access to the service:</b> Your access to the PicoxIA service will be deactivated
                on the effective date of termination. You will no longer be able to access features
                and content reserved for subscribers.
              </li>
              <li>
                <b>Refund:</b> If you have paid in advance, you may be entitled to a partial refund
                in proportion to the time used.
              </li>

              <li>
                <b>Personal Information:</b> In accordance with our Privacy Policy, your personal
                data will be processed in accordance with current legislation. You can request the
                deletion of your data by contacting our customer service department.
              </li>
            </ul>
          </li>
        </ul>
      </section>

      <section id="8-hypertext-links">
        <h2>8.&nbsp;Hypertext links</h2>
        <p>
          The site may contain hypertext links. Users are informed that by clicking on these links
          they leave the <a href="https://picoxia.com/en/">picoxia.com</a> website.
          <br />
          <a href="https://picoxia.com/en/">Picoxia.com</a> has no control over the websites to
          which these links lead and cannot be held responsible for their content.
        </p>
      </section>

      <section id="9-cookies">
        <h2>9.&nbsp;Cookie policy</h2>
        <p>
          The <a href="https://picoxia.com/en/">picoxia.com</a> website only uses cookies that are
          necessary for its operation. These cookies are essential to ensure navigation and use of
          the basic functions of the site. They do not collect personal information and are not used
          for tracking or marketing purposes.
        </p>
        <p>
          By using our site, you accept the use of these necessary cookies. If you have any
          questions, please contact us at{' '}
          <a href="mailto:contact@picoxia.com" target="_blank">
            contact@picoxia.com
          </a>
          .
        </p>
      </section>

      <section id="10-terms-of-use-changes">
        <h2>10.&nbsp;Changes to the GCU</h2>
        <p>
          Picoxia reserves the right to change these terms and conditions at any time. Changes will
          be published on the website with a date of entry into force. It is the User's
          responsibility to regularly consult the GCU in order to be aware of any changes. Continued
          use of the Site following the posting of changes will constitute acceptance of the new
          GCU.
        </p>
      </section>

      <section id="11-security">
        <h2>11.&nbsp;Security</h2>
        <p>
          Picoxia uses technical and organisational security measures to protect User's personal
          data against unauthorised access, disclosure, alteration or destruction. However, while we
          strive to protect your information, no method of transmission over the Internet or
          electronic storage can be guaranteed to be completely secure.
        </p>
      </section>

      <section id="12-applicable-law-and-jurisdiction">
        <h2>12.&nbsp;Applicable law and jurisdiction</h2>
        <p>
          This contract is governed by French law. If any dispute between the parties cannot be
          settled amicably, the French courts shall have exclusive jurisdiction.
        </p>
        <p>
          For any question relating to the application of these Terms and Conditions, you may
          contact the Publisher using the contact details provided in&nbsp;
          <IntlCrawlableLink url="/terms-of-use#1-legal-notices">article 1</IntlCrawlableLink>.
        </p>
      </section>
    </>
  );
}

/**
 * TermsOfUseContent
 * Display the content of the terms of use
 * @returns {JSX.Element}
 */
function TermsOfUseContent() {
  const locale = useSelector(selectLocale);

  if (locale === 'fr') {
    return <RenderFrenchText />;
  }

  return <RenderEnglishText />;
}

export default TermsOfUseContent;
